import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Loader2, MessageSquare, Reply } from "lucide-react";

import {
  createConstituentComplaintComment,
  listConstituentComplaintComments,
  type ConstituentComplaintComment,
} from "@/api/constituent_complaint_comments";
import {
  listConstituentComplaintEvents,
  type ConstituentComplaintEvent,
} from "@/api/constituent_complaint_events";
import { TRUST_COMPLAINT_STATUS_LABELS } from "@/api/trust_complaints";
import { groupComments } from "@/components/comments/groupComments";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { invalidateTrustSuitePipeline } from "@/lib/trustSuiteQueryKeys";

type ComplaintActivityProps = {
  municipalityId: number;
  complaintId: number;
  fetchEnabled?: boolean;
};

type ActivityEntry =
  | {
      kind: "comment";
      at: string;
      comment: ConstituentComplaintComment;
      replies: ConstituentComplaintComment[];
    }
  | {
      kind: "event";
      at: string;
      event: ConstituentComplaintEvent;
    };

export function buildComplaintActivity(
  events: ConstituentComplaintEvent[],
  comments: ConstituentComplaintComment[],
): ActivityEntry[] {
  const { topLevel, repliesByParent } = groupComments(comments);

  const entries: ActivityEntry[] = [
    ...topLevel.map((comment) => ({
      kind: "comment" as const,
      at: comment.created_at,
      comment,
      replies: repliesByParent.get(comment.id) ?? [],
    })),
    ...events.map((event) => ({
      kind: "event" as const,
      at: event.created_at,
      event,
    })),
  ];

  return entries.sort(
    (left, right) => new Date(right.at).getTime() - new Date(left.at).getTime(),
  );
}

function formatStatus(value: unknown): string {
  if (typeof value !== "string") return "—";
  return TRUST_COMPLAINT_STATUS_LABELS[value as keyof typeof TRUST_COMPLAINT_STATUS_LABELS] ?? value;
}

function formatEventSummary(event: ConstituentComplaintEvent): string {
  const metadata = event.metadata ?? {};
  switch (event.event_type) {
    case "status_changed":
      return `Status changed from ${formatStatus(metadata.from)} to ${formatStatus(metadata.to)}`;
    case "assignee_changed":
      return "Assignee updated";
    case "notes_changed":
      return "Internal notes updated";
    case "accepted_into_working":
      return "Moved to working queue";
    case "task_created":
      return typeof metadata.title === "string" ? `Task added: ${metadata.title}` : "Task added";
    case "task_completed":
      return typeof metadata.title === "string" ? `Task completed: ${metadata.title}` : "Task completed";
    case "task_reopened":
      return typeof metadata.title === "string" ? `Task reopened: ${metadata.title}` : "Task reopened";
    case "task_removed":
      return typeof metadata.title === "string" ? `Task removed: ${metadata.title}` : "Task removed";
    default:
      return String(event.event_type).replace(/_/g, " ");
  }
}

export function ComplaintActivity({
  municipalityId,
  complaintId,
  fetchEnabled = true,
}: ComplaintActivityProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyDraft, setReplyDraft] = useState("");

  const commentsQueryKey = ["constituent_complaint_comments", municipalityId, complaintId] as const;
  const eventsQueryKey = ["constituent_complaint_events", municipalityId, complaintId] as const;

  const { data: comments = [], isLoading: commentsLoading, error: commentsError } = useQuery({
    queryKey: commentsQueryKey,
    queryFn: () => listConstituentComplaintComments(municipalityId, complaintId),
    enabled: fetchEnabled,
  });

  const { data: events = [], isLoading: eventsLoading, error: eventsError } = useQuery({
    queryKey: eventsQueryKey,
    queryFn: () => listConstituentComplaintEvents(municipalityId, complaintId),
    enabled: fetchEnabled,
  });

  const activity = useMemo(
    () => buildComplaintActivity(events, comments),
    [events, comments],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: commentsQueryKey });
    queryClient.invalidateQueries({ queryKey: eventsQueryKey });
    invalidateTrustSuitePipeline(queryClient, municipalityId);
  };

  const createMutation = useMutation({
    mutationFn: ({ body, parentId }: { body: string; parentId?: number | null }) =>
      createConstituentComplaintComment(municipalityId, complaintId, body, parentId ?? undefined),
    onSuccess: () => {
      setDraft("");
      setReplyDraft("");
      setReplyingTo(null);
      invalidate();
    },
  });

  const loading = commentsLoading || eventsLoading;
  const feedError = commentsError || eventsError;

  return (
    <div className="space-y-4" data-testid="trust-complaint-activity">
      <p className="text-sm text-muted-foreground">
        Comments, status changes, and tasks in one timeline.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading activity…
        </div>
      ) : feedError ? (
        <p className="text-sm text-destructive">Could not load activity.</p>
      ) : activity.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-6 text-center">
          <MessageSquare className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            No activity yet. Post a comment below to start the conversation.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {activity.map((entry) => {
            if (entry.kind === "event") {
              return (
                <li
                  key={`event-${entry.event.id}`}
                  className="rounded-lg border border-dashed bg-background p-3"
                >
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <History className="h-3.5 w-3.5" />
                      <span className="font-medium text-foreground/80">{entry.event.actor_name}</span>
                    </div>
                    <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                  </div>
                  <p className="text-sm">{formatEventSummary(entry.event)}</p>
                </li>
              );
            }

            return (
              <li key={`comment-${entry.comment.id}`} className="rounded-lg border bg-muted/20 p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">{entry.comment.author_name}</span>
                  <time dateTime={entry.at}>{formatRelativeTime(entry.at)}</time>
                </div>
                <p className="whitespace-pre-wrap text-sm">{entry.comment.body}</p>

                {entry.replies.length > 0 ? (
                  <ul className="mt-3 space-y-2 border-l-2 border-muted pl-3">
                    {entry.replies.map((reply) => (
                      <li key={reply.id}>
                        <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground/80">{reply.author_name}</span>
                          <time dateTime={reply.created_at}>
                            {formatRelativeTime(reply.created_at)}
                          </time>
                        </div>
                        <p className="whitespace-pre-wrap text-sm">{reply.body}</p>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {replyingTo === entry.comment.id ? (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      value={replyDraft}
                      onChange={(event) => setReplyDraft(event.target.value)}
                      placeholder="Write a reply…"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={!replyDraft.trim() || createMutation.isPending}
                        onClick={() =>
                          createMutation.mutate({
                            body: replyDraft.trim(),
                            parentId: entry.comment.id,
                          })
                        }
                      >
                        Reply
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyDraft("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 h-8 px-2 text-xs"
                    onClick={() => setReplyingTo(entry.comment.id)}
                  >
                    <Reply className="mr-1.5 h-3.5 w-3.5" />
                    Reply
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="space-y-2">
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Add a comment for your team…"
          rows={3}
        />
        <Button
          type="button"
          disabled={!draft.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate({ body: draft.trim() })}
        >
          Post comment
        </Button>
      </div>
    </div>
  );
}
